import { strToU8, zipSync } from 'fflate';

export type XlsxCellFormat = 'text' | 'money' | 'ratio' | 'percent' | 'moneyDelta';

type ExportInput = {
  sheetName: string;
  headers: string[];
  rows: Array<Array<string | number | null>>;
  formats: XlsxCellFormat[];
  fileName: string;
};
const xmlEscape = (s: string) =>
  s
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

const colName = (colIndex1Based: number) => {
  let n = colIndex1Based;
  let s = '';
  while (n > 0) {
    const mod = (n - 1) % 26;
    s = String.fromCharCode(65 + mod) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};
const cellRef = (r: number, c: number) => `${colName(c)}${r}`;

const styleIndexOf = (fmt: XlsxCellFormat) => {
  switch (fmt) {
    case 'money':
      return 1;
    case 'ratio':
      return 2;
    case 'percent':
      return 3;
    case 'moneyDelta':
      return 4;
    default:
      return 0;
  }
};

const buildCell = (r: number, c: number, v: string | number, opts?: { s?: number; t?: 'inlineStr' | 'n' }) => {
  const ref = cellRef(r, c);
  const sAttr = opts?.s ? ` s="${opts.s}"` : '';
  if (opts?.t === 'inlineStr') {
    return `<c r="${ref}" t="inlineStr"${sAttr}><is><t>${xmlEscape(String(v))}</t></is></c>`;
  }
  return `<c r="${ref}"${sAttr}><v>${v}</v></c>`;
};

export const downloadXlsx = (input: ExportInput) => {
  const allRows = [input.headers, ...input.rows];

  const sheetRowsXml = allRows
    .map((row, rowIdx0) => {
      const r = rowIdx0 + 1;
      const cells: string[] = [];
      row.forEach((cell, colIdx0) => {
        const c = colIdx0 + 1;
        if (cell === null || cell === undefined || cell === '') return;

        if (rowIdx0 === 0) {
          cells.push(buildCell(r, c, String(cell), { t: 'inlineStr' }));
          return;
        }

        const fmt = input.formats[colIdx0] ?? 'text';
        const s = styleIndexOf(fmt);

        if (typeof cell === 'number') {
          cells.push(buildCell(r, c, cell, { t: 'n', s: s || undefined }));
        } else {
          cells.push(buildCell(r, c, String(cell), { t: 'inlineStr' }));
        }
      });
      return `<row r="${r}">${cells.join('')}</row>`;
    })
    .join('');

  const lastRow = allRows.length;
  const lastCol = input.headers.length || 1;
  const dimensionRef = `A1:${cellRef(lastRow, lastCol)}`;
  const sheetName = input.sheetName.slice(0, 31);

  const contentTypesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`;

  const relsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

  const workbookXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    <sheet name="${xmlEscape(sheetName)}" sheetId="1" r:id="rId1"/>
  </sheets>
</workbook>`;

  const workbookRelsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

  // styles: 0 default, 1 money, 2 ratio, 3 percent, 4 moneyDelta
  const stylesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <numFmts count="2">
    <numFmt numFmtId="165" formatCode="#,##0.0"/>
    <numFmt numFmtId="166" formatCode="+#,##0;-#,##0;0"/>
  </numFmts>
  <fonts count="1">
    <font><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font>
  </fonts>
  <fills count="1"><fill><patternFill patternType="none"/></fill></fills>
  <borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="5">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="3" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
    <xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
    <xf numFmtId="9" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
    <xf numFmtId="166" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
  </cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

  const sheetXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <dimension ref="${dimensionRef}"/>
  <sheetViews>
    <sheetView workbookViewId="0"/>
  </sheetViews>
  <sheetFormatPr defaultRowHeight="15"/>
  <sheetData>${sheetRowsXml}</sheetData>
</worksheet>`;

  const zipped = zipSync(
    {
      '[Content_Types].xml': strToU8(contentTypesXml),
      '_rels/.rels': strToU8(relsXml),
      'xl/workbook.xml': strToU8(workbookXml),
      'xl/_rels/workbook.xml.rels': strToU8(workbookRelsXml),
      'xl/worksheets/sheet1.xml': strToU8(sheetXml),
      'xl/styles.xml': strToU8(stylesXml),
    },
    { level: 6 }
  );

  const blob = new Blob([zipped], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = input.fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
